import { useEffect, useId } from "react";
import { toast } from "sonner";
import { Toaster } from "~/components/Toaster";
import { Minus, Plus } from "lucide-react";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { Text } from "~/components/Text";
import { useCheckoutSession } from "./CheckoutSession";
import { groupSelectionAvailable, groupTicketVariations } from "./group-checkout";
import { getTicketVariationAvailabilityLabel } from "./ticketVariations";
import { centsToMoney } from "~/utils/money";
import { useRootLocale } from "~/root";

type Props = {
  plan: EventFragment;
  selectedVariationID: string | null;
  onSelect: (id: string) => void;
};
export function GroupTicketSelection({
  plan,
  selectedVariationID,
  onSelect,
}: Props) {
  const { draft, update } = useCheckoutSession();
  const locale = useRootLocale();
  const slots = draft.groupSlots;
  const purchaseLimit = plan.allowGroupRSVPs ? plan.maxTicketsPerPurchase : 1;
  const toasterId = useId();
  const noticeId = `${toasterId}-ticket-limit`;
  useEffect(() => () => { toast.dismiss(noticeId); }, [noticeId]);
  useEffect(() => {
    if (
      slots.length ||
      !selectedVariationID ||
      draft.attempt ||
      draft.walletIntentID
    )
      return;
    const id = crypto.randomUUID();
    update({
      groupSlots: [{ id, variationID: selectedVariationID, recipientName: "" }],
      purchaserSlotID: id,
    });
  }, [
    slots.length,
    selectedVariationID,
    draft.attempt,
    draft.walletIntentID,
    update,
  ]);
  const setSlots = (next: typeof slots) => {
    const own =
      next.find((slot) => slot.id === draft.purchaserSlotID) ?? next[0];
    update({
      groupSlots: next,
      purchaserSlotID: own?.id ?? "",
      paymentPhase: "overview",
    });
    if (own) onSelect(own.variationID);
  };
  return (
    <div className="flex flex-col gap-3">
      {groupTicketVariations(plan.ticketVariations, slots)
        .filter(variation => variation.available || variation.checkoutStatus === "SOLD_OUT" || slots.some(slot => slot.variationID === variation.id))
        .map((variation) => {
          const availability = getTicketVariationAvailabilityLabel(variation);
          const soldOut = availability === "Sold out";
          const count = slots.filter(
            (slot) => slot.variationID === variation.id,
          ).length;
          const candidate = [
            ...slots,
            { id: "next", variationID: variation.id, recipientName: "" },
          ];
          return (
            <div
              key={variation.id}
              className={`overflow-hidden rounded-xl bg-white text-butter-green ${variation.available ? "" : "opacity-50"}`}
            >
              <div className="flex items-center gap-3 p-[0.6rem] sm:p-[0.8rem]">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl"
                >
                  {variation.emoji}
                </span>
                <div className="min-w-0 flex-1 wrap-break-word">
                  <Text weight="bold">{variation.name}</Text>
                  {variation.description && (
                    <Text size="sm">{variation.description}</Text>
                  )}
                  {availability && !soldOut && (
                    <Text size="sm">
                      {availability}
                    </Text>
                  )}
                </div>
                <Text as="span" variant={soldOut ? "secondary" : "primary"} size={soldOut ? "sm" : "xs"} className="shrink-0">
                  {soldOut ? "Sold out" : centsToMoney(variation.totalCostCents, "$", locale)}
                </Text>
              </div>
              {(variation.available || count > 0) && (
                <div className="flex items-center justify-between gap-3 border-t border-butter-cream-light px-[0.6rem] py-[0.4rem] sm:px-[0.8rem]">
                  <Text size="sm" weight="semibold">How many?</Text>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="flex size-11 shrink-0 items-center justify-center rounded-full transition-colors enabled:cursor-pointer enabled:hover:bg-butter-cream-light focus-visible:bg-butter-cream disabled:opacity-40"
                      aria-label={`Remove ${variation.name} ticket`}
                      disabled={!count || slots.length <= 1}
                      onClick={() => {
                        const removed =
                          [...slots]
                            .reverse()
                            .find(
                              (slot) =>
                                slot.variationID === variation.id &&
                                slot.id !== draft.purchaserSlotID,
                            ) ??
                          [...slots]
                            .reverse()
                            .find((slot) => slot.variationID === variation.id);
                        setSlots(slots.filter((slot) => slot.id !== removed?.id));
                      }}
                    >
                      <span className="flex size-8 items-center justify-center rounded-full border border-butter-green">
                        <Minus aria-hidden className="size-4" />
                      </span>
                    </button>
                    <span aria-live="polite" className="min-w-5 text-center font-semibold tabular-nums">{count}</span>
                    <button
                      type="button"
                      className="flex size-11 shrink-0 items-center justify-center rounded-full transition-colors enabled:cursor-pointer enabled:hover:bg-butter-cream-light focus-visible:bg-butter-cream disabled:opacity-40"
                      aria-label={`Add ${variation.name} ticket`}
                      disabled={!groupSelectionAvailable(candidate, plan)}
                      onClick={() => {
                        if (!groupSelectionAvailable(candidate, plan)) return;
                        const next = [
                          ...slots,
                          { id: crypto.randomUUID(), variationID: variation.id, recipientName: "" },
                        ];
                        setSlots(next);
                        if (next.length >= purchaseLimit) {
                          toast.info(`You’ve reached the limit of ${purchaseLimit} ${purchaseLimit === 1 ? "ticket" : "tickets"} per booking.`, { id: noticeId, toasterId });
                        } else if (variation.ticketsRemaining != null && count + 1 >= variation.ticketsRemaining) {
                          toast.info(`All remaining ${variation.name} tickets are in your bag.`, { id: noticeId, toasterId });
                        }
                      }}
                    >
                      <span className="flex size-8 items-center justify-center rounded-full bg-butter-green text-butter-cream-light">
                        <Plus aria-hidden className="size-4" />
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      <Toaster id={toasterId} />
    </div>
  );
}
